import { lazy, useContext, useEffect, useState, useRef, useCallback, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import AuthContext from '../../contexts/authContext';
import Login from '../../pages/presentation/auth/Login';
import verifyAccess from '../../utils/verifyAccess';
import AccessContext from '../../contexts/accessContext';
import { getAPI } from '../../apis/backend/apiNode';
import Plan from '../../pages/presentation/auth/Plan';
import PageWrapper from '../PageWrapper/PageWrapper';
import SubHeader from '../SubHeader/SubHeader';
import Page from '../Page/Page';
import Card, { CardBody } from '../../components/bootstrap/Card';
import DocumentLayout from '../../pages/DocumentLayout/DocumentLayout';
import Checkout from '../../pages/ClientActions/paiement/Checkout';
import Confirmation from '../../pages/ClientActions/paiement/ConfirmationPage';
import AcceptDevisMail from '../../pages/ClientActions/Signature/AcceptDevisMail';
// import Paiement from '../pages/MClientActionsail/paiement/Checkout';
// import Confirmation from '../pages/ClientActions/paiement/ConfirmationPage';

const PAGE_404 = lazy(() => import('../../pages/presentation/auth/Page404'));
const LOGIN_PAGE = lazy(() => import('../../pages/presentation/auth/Login'));

export const ClientActionMenu = {
	acceptDevis: {
		id: 'signature',
		text: 'Signature devis',
		path: 'signature-devis',
		icon: 'check_box_outline_blank ',
		hide: true,
	},
	paiement: {
		id: 'paiement',
		text: 'Paiement Store2000',
		path: 'paiement',
		icon: 'Payments',
		hide: true,
	},
	confirmation: {
		id: 'confirmation',
		text: 'Confirmation Paiement Store2000',
		path: 'confirmation',
		icon: 'DoneAll',
		hide: true,
	},
};
const ContentRoutes = () => {
	const { userData } = useContext(AuthContext);
	const { setAccess } = useContext(AccessContext);
	const [isLoadingFinish, setLoadingFinish] = useState(false);
	const hasFetched = useRef(false); // Prevent duplicate fetches

	// Base routes that should always be available
	const baseRoutes = [
		{
			id: '404',
			path: '*',
			element: <PAGE_404 />,
			exact: true,
		},
		{
			id: 'login',
			path: 'auth-pages/login',
			element: (
				<Suspense fallback={LOADING}>
					<Login />
				</Suspense>
			),
			exact: true,
		},
		{
			id: 'plan',
			path: 'plan',
			element: <Plan />,
			exact: true,
		},
		{
			id: 'DocumentLayout',
			path: 'document-layouts/download/:model/:id',
			element: <DocumentLayout />,
			exact: true,
		},
		{
			path: `${ClientActionMenu.paiement.path}/:model/:id/:token`,
			element: <Checkout />,
			exact: true,
		},
		/* Confirmation Page*/

		{
			path: `${ClientActionMenu.confirmation.path}`,
			element: <Confirmation />,
			exact: true,
		},
		{
			path: `${ClientActionMenu.acceptDevis.path}/:id/:token`,
			element: <AcceptDevisMail />,
			exact: true,
		},
	];

	const [modulesMenu, setModulesMenu] = useState<any[]>(baseRoutes);

	const getAccess = useCallback(async () => {
		try {
			const data = await getAPI('employe', '/access');
			setAccess(data);
		} catch (error) {
		}
	}, [setAccess]);

	const fetchDynamicModules = async () => {
		try {
			const modules = await getAPI('module');
			const newModulesMenu: any[] = [];
			if (Array.isArray(modules) && modules.length > 0) {
				await Promise.all(
					modules.map(async (module: any) => {
						if (Array.isArray(module?.subModules) && module.subModules.length > 0) {
							module.subModules.forEach((subModule: any) => {
								const LoadedPageModule = lazy(
									() =>
										import(`../../pages/${subModule.route.pageComponentPath}`),
								);

								newModulesMenu.push({
									id: subModule.id,
									path: subModule.route.path,
									element: (
										<LoadedPageModule
											hasAccess={(access: string) =>
												verifyAccess(subModule?.access, access)
											}
										/>
									),
									exact: true,
								});

								if (Array.isArray(subModule?.othersPagesPath)) {
									subModule.othersPagesPath.forEach((route: any) => {
										const LoadedPage = lazy(
											() => import(`../../pages/${route.pageComponentPath}`),
										);
										newModulesMenu.push({
											id: route.id,
											path: route.path,
											element: (
												<LoadedPage
													hasAccess={(access: string) =>
														verifyAccess(subModule?.access, access)
													}
												/>
											),
											exact: true,
										});
									});
								}
							});
						}
					}),
				);
			}
			return newModulesMenu;
		} catch (error) {
			return [];
		}
	};

	const getSupAdminRoutes = () => {
		const PagesSocietes = lazy(() => import('../../pages/Companies/AllCompanies'));
		const SingleSociete = lazy(() => import('../../pages/Companies/SingleSociety'));
		const PagesModules = lazy(() => import('../../pages/presentation/Modules'));
		const WELCOMING_PAGE_SUPADMIN = lazy(
			() => import('../../layout/Content/WelcomingPageSupAdmin'),
		);
		const PagesComptes = lazy(() => import('../../pages/Utilisateurs'));
		const COMPTES_SINGLE = lazy(() => import('../../pages/Utilisateurs/SingleCompte'));
		const COMPTES_SINGLE_ACCESS = lazy(
			() => import('../../pages/Utilisateurs/SingleCompteAccess'),
		);
		const COMPTE_DETAILS = lazy(() => import('../../pages/Company/CompanyDetails'));
		const PageRoles = lazy(() => import('../../pages/Roles'));
		const PageSingleRole = lazy(() => import('../../pages/Roles/SingleRole'));
		const PageSessions = lazy(() => import('../../pages/Sessions/SessionsListPage'));
		const PageConfig = lazy(() => import('../../pages/Config/Configuration'));
		const PageEmailsTemplates = lazy(() => import('../../pages/Emails/EmailTemplatesList'));
		const PageEmailsTemplatesDetails = lazy(
			() => import('../../pages/Emails/EmailTemplateForm'),
		);

		return [
			{
				id: 'config',
				path: 'configuration',
				element: <PageConfig />,
				exact: true,
			},
			{
				id: 'gestionEnterprise',
				path: 'societes',
				element: <PagesSocietes />,
				exact: true,
			},
			{
				id: 'gestionEnterpriseSingle',
				path: 'societes/:id',
				element: <SingleSociete />,
				exact: true,
			},
			{
				id: 'gestionModules',
				path: 'modules',
				element: <PagesModules />,
				exact: true,
			},
			{
				id: 'gestionModulesSingle',
				path: 'modules/:id',
				element: <PagesModules />,
				exact: true,
			},
			{
				id: 'welcome',
				path: '/',
				element: <WELCOMING_PAGE_SUPADMIN />,
				exact: true,
			},
			{
				id: 'gestionComptes',
				path: 'comptes',
				element: <PagesComptes />,
				exact: true,
			},

			{
				id: 'gestionComptesSingle',
				path: `comptes/:id`,
				element: <COMPTES_SINGLE />,
				exact: true,
			},
			{
				id: 'SingleCompteAccess',
				path: `comptes/:id/access/:id_module`,
				element: <COMPTES_SINGLE_ACCESS />,
				exact: true,
			},
			{
				id: 'SingleCompteDetails',
				path: `company/info`,
				element: <COMPTE_DETAILS />,
				exact: true,
			},
			{
				id: 'gestionRoles',
				path: 'roles',
				element: <PageRoles />,
				exact: true,
			},

			{
				id: 'gestionSingleRoles',
				path: `roles/:id`,
				element: <PageSingleRole />,
				exact: true,
			},
			{
				id: 'gestionSessions',
				path: `sessions`,
				element: <PageSessions />,
				exact: true,
			},
			{
				id: 'EmailsTemplates',
				path: 'email/templates',
				element: <PageEmailsTemplates />,
				exact: true,
			},
			{
				id: 'EmailsTemplatesDetails',
				path: `/email/:id`,
				element: <PageEmailsTemplatesDetails />,
				exact: true,
			},
		];
	};
	const initializeUserRoutes = useCallback(async () => {
		try {
			const dynamicModules = await fetchDynamicModules();
			let allUserRoutes = [...dynamicModules];

			if (userData?.role === 'sup_admin') {
				const supAdminRoutes = getSupAdminRoutes();
				allUserRoutes = [...allUserRoutes, ...supAdminRoutes];
			} else {
				await getAccess();
			}

			const filteredBase = baseRoutes.filter(r => r.path !== '*');
        const routeNotFOUND = baseRoutes.find(r => r.path === '*');

        // 2. Combine: Static -> Dynamic -> 404 at the very end
        const finalRoutes = [...filteredBase, ...allUserRoutes, routeNotFOUND];

			setModulesMenu(finalRoutes);
		} catch (error) {
		} finally {
			setLoadingFinish(true);
		}
	}, [userData?.role, fetchDynamicModules, getSupAdminRoutes, getAccess]);

	// Main effect
	useEffect(() => {
		if (!userData?.id || hasFetched.current) {
			setLoadingFinish(true);
			return;
		}

		hasFetched.current = true;
		initializeUserRoutes();
	}, [userData, initializeUserRoutes]);

	return isLoadingFinish ? (
		<Routes>
			{modulesMenu?.map((route, idx) => (
				<Route key={idx} path={route.path} element={route.element} />
			))}
		</Routes>
	) : (
		LOADING
	);
};

export default ContentRoutes;

const LOADING = (
	<PageWrapper isProtected={false} title='content'>
		<SubHeader>
			<div />
		</SubHeader>
		<Page>
			<div className='row h-100'>
				<div className='col-lg-6'>
					<Card stretch>
						<CardBody>
							<div />
						</CardBody>
					</Card>
				</div>
				<div className='col-lg-6'>
					<Card stretch='semi'>
						<CardBody>
							<div />
						</CardBody>
					</Card>
					<Card stretch='semi'>
						<CardBody>
							<div />
						</CardBody>
					</Card>
				</div>
			</div>
		</Page>
	</PageWrapper>
);
